import { motion } from 'framer-motion';
import { CheckCircle, ClipboardList, QrCode, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { trackCTA } from '../utils/analytics';

const STEP_ICONS = [QrCode, ShieldCheck, ClipboardList];

const buildStructuredData = config => [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: config.structuredDataName,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR', description: 'Free pilot' },
    description: config.structuredDataDescription,
    provider: { '@type': 'Organization', name: 'IDGate', url: 'https://idgate.in' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.faq.items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  },
];

const VerticalPage = ({ config }) => {
  const { seo, hero, painPoints, steps, benefits, faq, cta, slug, onlineUses } = config;

  return (
    <>
      <SEOHead
        title={seo.title}
        absoluteTitle={`${seo.title} | IDGate`}
        description={seo.description}
        keywords={seo.keywords}
        url={seo.url}
        structuredData={buildStructuredData(config)}
      />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="container-custom py-20 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">{hero.eyebrow}</p>
            <h1 className="section-title mt-3">
              {hero.h1.split('\n').map((line, i, arr) => (
                <span key={i}>
                  {line}
                  {i < arr.length - 1 && <br className="hidden sm:block" />}
                </span>
              ))}
            </h1>
            <p className="mt-6 text-lg text-slate-600">{hero.sub}</p>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Link
                to="/contact"
                className="btn-primary"
                onClick={() => trackCTA(`start_pilot_${slug}`, `${slug}_hero`)}
              >
                Start free pilot
              </Link>
              <Link
                to="/demo"
                className="btn-secondary"
                onClick={() => trackCTA(`view_demo_${slug}`, `${slug}_hero`)}
              >
                See how it works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="bg-slate-50 py-16">
        <div className="container-custom">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="section-title">{painPoints.h2}</h2>
            <p className="mt-4 text-slate-600">{painPoints.sub}</p>
          </div>
          <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {painPoints.items.map(item => (
              <div key={item} className="rounded-xl border border-red-100 bg-white p-5 shadow-sm">
                <p className="text-sm font-medium text-slate-700">&#x2717;&nbsp;&nbsp;{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <h2 className="section-title text-center">How IDGate works at your entrance</h2>
          <div className="mx-auto mt-12 grid max-w-4xl gap-8 sm:grid-cols-3">
            {steps.map((step, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
                >
                  <Icon className="h-8 w-8 text-primary-600" aria-hidden />
                  <h3 className="mt-4 font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{step.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Online / embed */}
      {onlineUses && onlineUses.length > 0 && (
        <section className="bg-slate-50 py-16">
          <div className="container-custom">
            <div className="mx-auto max-w-4xl">
              <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
                <div>
                  <p className="eyebrow">Beyond the front desk</p>
                  <h2 className="section-title mt-3">Works online, not just at your entrance</h2>
                  <p className="mt-4 text-slate-600">
                    Your IDGate check-in URL works as a shareable link or an embeddable widget — so verification happens before people arrive, not after.
                  </p>
                  <ul className="mt-6 space-y-3">
                    {onlineUses.map(use => (
                      <li key={use} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" aria-hidden />
                        <span className="text-sm text-slate-700">{use}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="btn-primary mt-8 inline-block"
                    onClick={() => trackCTA(`online_embed_${slug}`, `${slug}_online`)}
                  >
                    Get your verification link
                  </Link>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">Share as a link</p>
                  <div className="rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-500">
                    https://app.idgate.in/checkin
                  </div>
                  <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-widest text-slate-400">Or embed on your website</p>
                  <div className="rounded-lg bg-slate-900 p-3 font-mono text-xs leading-relaxed text-slate-300">
                    {'<iframe\n  src="https://app.idgate.in/checkin"\n  width="100%" height="600"\n/>'}
                  </div>
                  <p className="mt-4 text-xs text-slate-400">
                    Check-in URLs are generated in your IDGate dashboard — copy, paste, done.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="bg-primary-700 py-16 text-white">
        <div className="container-custom">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-2xl font-bold tracking-tight">What IDGate adds to your operation</h2>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {benefits.map(benefit => (
                <li key={benefit} className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent-400" aria-hidden />
                  <span className="text-sm text-primary-100">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <h2 className="section-title text-center">{faq.h2}</h2>
          <div className="mx-auto mt-10 max-w-2xl divide-y divide-slate-200">
            {faq.items.map(item => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between font-medium text-slate-900 marker:content-none">
                  {item.q}
                  <span className="ml-4 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180">
                    &#9662;
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-50 py-16">
        <div className="container-custom text-center">
          <h2 className="section-title">{cta.h2}</h2>
          <p className="mt-4 text-slate-600">{cta.sub}</p>
          <Link
            to="/contact"
            className="btn-primary mt-8 inline-block"
            onClick={() => trackCTA(`start_pilot_${slug}`, `${slug}_footer_cta`)}
          >
            {cta.btn}
          </Link>
        </div>
      </section>
    </>
  );
};

export default VerticalPage;
